import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import {
  getPreLeadGoogleAdsFields,
  getPreLeadMetaFields,
  getPreLeadWebsiteFormFields
} from '../../services/api/implementation/impl';
import { formatStatusLabel } from '../../utils/stringUtils';

function formatMetaFieldQuestion(question) {
  if (!question) return '—';
  return String(question)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Map CRM lead.source (or prelead list source) → form-fields channel.
 * @returns {'facebook'|'google_ads'|'website'|'none'}
 */
export function resolvePreleadFormChannel(source) {
  const raw = String(source ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return 'none';

  if (
    raw === 'facebook' ||
    raw === 'instagram' ||
    raw.includes('facebook') ||
    raw.includes('instagram')
  ) {
    return 'facebook';
  }

  if (raw === 'google_ads' || raw === 'google ads' || raw.includes('google')) {
    return 'google_ads';
  }

  if (
    raw === 'website' ||
    raw === 'company website' ||
    raw.includes('website') ||
    raw.includes('framer')
  ) {
    return 'website';
  }

  return 'none';
}

const CHANNEL_META = {
  facebook: {
    title: 'Facebook Form Responses',
    formMetaLabel: 'Form',
    queryKey: 'preLeadMetaFields',
    fetchFn: getPreLeadMetaFields,
    formMetaKey: 'form'
  },
  google_ads: {
    title: 'Google Ads Form Responses',
    formMetaLabel: 'Source',
    queryKey: 'preLeadGoogleAdsFields',
    fetchFn: getPreLeadGoogleAdsFields,
    formMetaKey: 'source'
  },
  website: {
    title: 'Website / Framer Form Responses',
    formMetaLabel: 'Form',
    queryKey: 'preLeadWebsiteFormFields',
    fetchFn: getPreLeadWebsiteFormFields,
    formMetaKey: 'source'
  }
};

function PreleadFormSummaryCard({ prelead, formMeta, formMetaLabel = 'Form' }) {
  const email = prelead?.email?.trim() || '—';
  const status = prelead?.state ? formatStatusLabel(String(prelead.state)) : '—';
  const formName = formMeta?.name || null;
  const projectName = formMeta?.project_name?.trim() || null;

  if (!prelead && !formMeta) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {formMetaLabel}
        </span>
        {formName && <span className="text-sm font-medium text-gray-900">{formName}</span>}
        {projectName && <span className="text-xs text-gray-500">· {projectName}</span>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="text-gray-700">
          <span className="text-gray-400">Email </span>
          <span className="font-medium text-gray-900 break-all">{email}</span>
        </span>
        {prelead?.state != null && (
          <span className="text-gray-700">
            <span className="text-gray-400">Status </span>
            <span className="font-medium text-gray-900">{status}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function FormFieldCards({ fields }) {
  if (!fields?.length) {
    return <p className="text-sm text-gray-500">No form fields available.</p>;
  }

  return fields.map((field, index) => (
    <div
      key={field.id ?? `${field.question}-${index}`}
      className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="flex items-start gap-2">
        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
          <p className="mt-1 text-sm font-medium text-gray-900">
            {formatMetaFieldQuestion(field.question)}
          </p>
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Answer</p>
        <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
          {field.answer || '—'}
        </p>
      </div>
    </div>
  ));
}

/**
 * Modal showing original inbound form answers for a prelead-backed CRM lead.
 */
export default function PreleadOriginalFormModal({
  preLeadId,
  source,
  prelead = null,
  onClose
}) {
  const channel = resolvePreleadFormChannel(source);
  const meta = channel !== 'none' ? CHANNEL_META[channel] : null;
  const numericId = Number(preLeadId);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [meta?.queryKey, numericId],
    queryFn: () => meta.fetchFn(numericId),
    enabled: Boolean(meta) && Number.isFinite(numericId) && numericId > 0
  });

  const fields = data?.fields ?? [];
  const formMeta = meta ? data?.[meta.formMetaKey] : null;
  const title = meta?.title ?? 'Prelead Form Responses';

  return (
    <Modal width={50} name={title} onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {channel === 'none' ? (
          <p className="text-sm text-gray-600">
            No original form submission is stored for this lead source
            {source ? ` (${source})` : ''}. Form answers are available for Facebook, Google Ads,
            and Website / Framer preleads.
          </p>
        ) : (
          <>
            {(prelead || formMeta || isLoading) && (
              <PreleadFormSummaryCard
                prelead={prelead}
                formMeta={formMeta}
                formMetaLabel={meta.formMetaLabel}
              />
            )}
            {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
            {isError && (
              <p className="text-sm text-red-700">
                {error?.response?.data?.message ||
                  error?.message ||
                  'Could not load form responses.'}
              </p>
            )}
            {!isLoading && !isError && <FormFieldCards fields={fields} />}
          </>
        )}
      </div>
    </Modal>
  );
}
